import { useMediaQuery } from "@material-ui/core";
import React from "react";
import Skeleton from "react-loading-skeleton";
import {
  Area,
  AreaChart,
  ResponsiveContainer,
  XAxis,
  YAxis,
  Legend,
  CartesianGrid,
  Tooltip,
  TooltipProps,
  Coordinate,
  TooltipPayload,
  LegendProps,
} from "recharts";
import { AllocationHistory } from "types/AllocationHistory";
import { dateToString, dateToMonthAndYear } from "utility/date";
import { graphColors, hexToRgb } from "utility/graphColors";
import { asEur, asPercent } from "utility/numbers";
import ChartLoader from "./ChartLoader";

interface Props {
  data: AllocationHistory[];
  loading?: boolean;
}

const CustomTooltip = ({ active, payload }: TooltipProps) => {
  if (active) {
    return (
      <p
        className="intro"
        style={{
          color: "#444443",
          fontWeight: 300,
          background: "rgb(255, 255, 255, 0.5)",
          padding: 4,
        }}
      >
        {dateToString((payload ?? [])[0]?.payload?.Month as Date | undefined)}
      </p>
    );
  }

  return <></>;
};

const renderLegend = (
  props: LegendProps,
  legendValues: number[],
  matchesMediaQuery: boolean
) => {
  const { payload } = props;

  return (
    <ul
      style={{
        paddingRight: 0,
        paddingTop: 0,
        paddingBottom: 0,
        paddingLeft: matchesMediaQuery ? 60 : 30,
        margin: 0,
        display: matchesMediaQuery ? "flex" : "grid",
        gridColumn: "auto",
        gridTemplateColumns: "repeat(2, 1fr)",
        justifyContent: "left",
        flexDirection: "row",
        flexWrap: "wrap",
        flexFlow: "row wrap",
        listStyle: "none",
        fontSize: "min(max(.8em, 3vw), 1em)",
      }}
    >
      {payload && payload.length > 0 ? (
        payload.map((entry, index) => {
          const summaEur = asEur(legendValues[index ?? 0]);
          const summaPros =
            summaEur !== "-"
              ? `(${asPercent(
                  (legendValues[index ?? 0] /
                    legendValues
                      .filter((x) => x !== undefined)
                      .reduce((p, c) => p + c, 0)) *
                    100
                )})`
              : "";

          return (
            <li
              key={`item-${index}`}
              style={{
                paddingBottom: matchesMediaQuery ? "0" : "5px",
                height: 35,
                width: matchesMediaQuery ? "33%" : "fit-content",
                verticalAlign: "top",
                display: "flex",
                flexDirection: "row",
              }}
            >
              <div
                style={{
                  marginTop: 3,
                  width: 14,
                  height: 14,
                  background: hexToRgb(entry.color ?? "#FFFFFF", 0.5),

                  border: `1px solid ${entry.color}`,
                  boxSizing: "border-box",
                  borderRadius: "50%",
                  marginRight: 9,
                }}
              />
              <div style={{ display: "flex", alignItems: "center" }}>
                <p>
                  {entry.value}

                  <br></br>
                  <span
                    style={{
                      color: "#868686",
                      fontSize: 10,
                      verticalAlign: "text-top",
                    }}
                  >
                    {legendValues.length > 0 ? (
                      `${summaEur} ${summaPros}`
                    ) : (
                      <div style={{ height: 18 }} />
                    )}
                  </span>
                </p>
              </div>
            </li>
          );
        })
      ) : (
        <>
          <li
            style={{
              height: 35,
              width: "33%",
            }}
          >
            <Skeleton height={15} width="80%" />
          </li>
          <li
            style={{
              height: 35,
              width: "33%",
            }}
          >
            <Skeleton height={15} width="80%" />
          </li>
          <li
            style={{
              height: 35,
              width: "33%",
            }}
          >
            <Skeleton height={15} width="80%" />
          </li>
        </>
      )}
    </ul>
  );
};

const AllocationChart = (props: Props) => {
  const { data, loading } = props;

  const matchesMediaQuery = useMediaQuery("(min-width: 600px)");

  const groupedObject = data.reduce((r, a) => {
    r[a.Month.toString()] = [...(r[a.Month.toString()] || []), a];
    return r;
  }, {} as { [key: string]: AllocationHistory[] });

  const categories = data.reduce((r, a) => {
    if (!r.includes(a.Category)) {
      r.push(a.Category);
    }
    return r;
  }, [] as string[]);

  const flattenedData = Object.keys(groupedObject).map((k) => {
    const item = groupedObject[k];

    const res: { Month: string; [key: string]: string | number } = { Month: k };

    item.forEach((i) => {
      res[i.Category] = i.Value;
    });

    return res;
  });

  let legendValues = [] as number[];

  // tslint:disable-next-line: no-object-literal-type-assertion
  const tooltipPosition = { y: 0 } as Coordinate;

  const tickformatterAmount = () => {
    const maxValue = Math.max(...data.map((d) => d.Value));

    if (maxValue > 1000000) {
      return {
        unit: "M€",
        formatter: 1000000,
      };
    } else if (maxValue > 1000) {
      return {
        unit: "k€",
        formatter: 1000,
      };
    }

    return {
      unit: "€",
      formatter: 1,
    };
  };

  const categoriesLength = categories.length > 0 ? categories.length - 1 : 0;

  return (
    <>
      <ResponsiveContainer
        height={
          Math.floor(categoriesLength / (matchesMediaQuery ? 3 : 2)) * 35 + 300
        }
        width="100%"
      >
        <AreaChart
          data={flattenedData}
          margin={{
            top: 5,
            right: matchesMediaQuery ? 30 : 0,
            left: matchesMediaQuery ? 20 : 0,
            bottom: 5,
          }}
          onMouseLeave={() => {
            legendValues = [];
          }}
          onMouseMove={(chartProps: { activePayload?: TooltipPayload[] }) => {
            const { activePayload } = chartProps;

            legendValues = activePayload?.map((x) => x.value as number) ?? [];
          }}
          height={225}
        >
          <CartesianGrid vertical={false} fill="white" color="#DDDDDD" />
          <XAxis
            dataKey="Month"
            tickFormatter={dateToMonthAndYear}
            interval={
              matchesMediaQuery
                ? Math.round(flattenedData.length / 8)
                : Math.round(flattenedData.length / 5)
            }
          />
          <YAxis
            allowDataOverflow
            allowDecimals={false}
            scale="linear"
            tickFormatter={(value: number) =>
              value / tickformatterAmount().formatter
            }
            label={{
              value: tickformatterAmount().unit,
              position: matchesMediaQuery ? "insideTopLeft" : "right",
            }}
            width={matchesMediaQuery ? 60 : 35}
          />
          <Tooltip
            cursor={{
              stroke: "#ACACAC",
              strokeWidth: 1,
              strokeDasharray: "2",
            }}
            active={false}
            content={CustomTooltip}
            position={tooltipPosition}
          />
          {categories.map((c, i) => (
            <Area
              key={c}
              type="monotone"
              stackId="1"
              dataKey={c}
              stroke={
                (graphColors[categories.length - 1] ??
                  graphColors[graphColors.length - 1])[i] ?? "#FFFFFF"
              }
              fill={hexToRgb(
                (graphColors[categories.length - 1] ??
                  graphColors[graphColors.length - 1])[i] ?? "#FFFFFF"
              )}
              fillOpacity={0.7}
            />
          ))}
          <Legend
            iconType="circle"
            content={(legendProps: LegendProps) =>
              renderLegend(legendProps, legendValues, matchesMediaQuery)
            }
          />
        </AreaChart>
      </ResponsiveContainer>
      {loading && (
        <div
          style={{
            height: 225,
            paddingBottom:
              Math.floor(categoriesLength / (matchesMediaQuery ? 3 : 2)) * 35,
            width: "100%",
            position: "absolute",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            top: 50,
            left: 0,
          }}
        >
          <ChartLoader />
        </div>
      )}
    </>
  );
};

export default React.memo(AllocationChart);
